import * as THREE from 'three';

// @ts-ignore
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';

export type Text3d = {
  getText: (
    text: string,
    maxLineLength: number,
    fontSize: 'sm' | 'md',
  ) => THREE.Group;
  material: THREE.MeshBasicMaterial;
};

export const TextPostion = new THREE.Vector2(0, 0.8);

export type Text3dOptions = {
  textOffset?: THREE.Vector3;
  textColor?: string;
};

export async function use3dText(options?: Text3dOptions): Promise<Text3d> {
  const loader = new FontLoader();
  const font = await loader.loadAsync(
    '/fonts/CircularXX TT Medium_Regular.json',
  );
  const material = new THREE.MeshBasicMaterial({
    color: options?.textColor ?? '#ededed',
    transparent: true,
  });

  return {
    getText,
    material,
  };

  function getText(
    text: string,
    maxLineLength: number = 30,
    fontSize: 'sm' | 'md' = 'sm',
  ) {
    const fontSizes = {
      sm: 1.6 * 0.5,
      md: 1.8,
    };

    const lineHeight = {
      sm: 0.28 * 0.5,
      md: 0.22,
    };

    const group = new THREE.Group();
    const textLines = splitStringIntoChunks(text, maxLineLength);

    for (let i = 0; i < textLines.length; i++) {
      const textLine = textLines[i];

      const geometry = new TextGeometry(textLine, {
        font,
        size: fontSizes[fontSize],
        depth: 0.01,
        curveSegments: 12,
        bevelEnabled: false,
        bevelThickness: 0.05,
        bevelSize: 0.05,
        bevelOffset: 0,
        bevelSegments: 5,
      });

      const textOffset = options?.textOffset
        ? options.textOffset
        : new THREE.Vector3(0, 0.9, 0);

      const mesh = new THREE.Mesh(geometry, material);
      mesh.scale.set(0.1, 0.1, 0.1);
      mesh.position.set(
        0 + textOffset.x,
        lineHeight[fontSize] * -i + textOffset.y,
        0 + textOffset.z,
      );

      group.add(mesh);
    }

    const longestMesh = group.children.reduce((longest, current) => {
      const boundingBox = new THREE.Box3().setFromObject(current);
      const longestBoundingBox = new THREE.Box3().setFromObject(longest);
      return boundingBox.max.x > longestBoundingBox.max.x ? current : longest;
    }, group.children[0]);

    for (const mesh of group.children) {
      const boundingBox = new THREE.Box3().setFromObject(mesh);
      const longestBoundingBox = new THREE.Box3().setFromObject(longestMesh);
      mesh.position.x += (longestBoundingBox.max.x - boundingBox.max.x) / 2;
    }

    group.position.x = TextPostion.x;
    group.position.y =
      TextPostion.y + textLines.length * 0.5 * lineHeight[fontSize];

    return group;
  }
}

function splitStringIntoChunks(
  inputString: string,
  chunkSize: number = 40,
): string[] {
  const chunks: string[] = [];
  let currentChunk = '';
  const words = inputString.split(' ');

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (currentChunk.length + word.length + 1 <= chunkSize) {
      currentChunk += (currentChunk ? ' ' : '') + word;
    } else {
      chunks.push(currentChunk);
      currentChunk = word;
    }
  }

  if (currentChunk) {
    chunks.push(currentChunk);
  }

  return chunks;
}
